import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plane, MapPin } from "lucide-react";

// Lightweight IATA hints based on a coarse lat/lng grid. Covers major user
// regions; falls back to manual entry if no match.
const REGIONAL_HUBS = [
    { lat: [24, 49], lng: [-125, -66], code: "JFK", name: "New York JFK (default US)" },
    { lat: [-39, -10], lng: [110, 155], code: "SYD", name: "Sydney" },
    { lat: [35, 71], lng: [-12, 32], code: "LHR", name: "London Heathrow (default Europe)" },
    { lat: [-35, 5], lng: [-82, -34], code: "GRU", name: "São Paulo Guarulhos" },
    { lat: [-35, 38], lng: [-20, 52], code: "JNB", name: "Johannesburg (default Africa/ME)" },
    { lat: [5, 55], lng: [60, 150], code: "SIN", name: "Singapore (default Asia)" },
];

function guessAirport(lat, lng) {
    for (const h of REGIONAL_HUBS) {
        if (lat >= h.lat[0] && lat <= h.lat[1] && lng >= h.lng[0] && lng <= h.lng[1]) return h;
    }
    return null;
}

export default function PreferredAirportCard({ user, onUpdated }) {
    const [code, setCode] = useState(user?.preferred_airport || "");
    const [busy, setBusy] = useState(false);
    const [hint, setHint] = useState(null);
    const [error, setError] = useState("");

    // Sync prop -> state when parent reloads user (render-time pattern, no effect needed).
    const [prevPrefAirport, setPrevPrefAirport] = useState(user?.preferred_airport);
    if (user?.preferred_airport !== prevPrefAirport) {
        setPrevPrefAirport(user?.preferred_airport);
        setCode(user?.preferred_airport || "");
    }

    const detect = () => {
        setError("");
        if (!navigator.geolocation) {
            setError("Your browser doesn't expose location services.");
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const guess = guessAirport(pos.coords.latitude, pos.coords.longitude);
                if (guess) {
                    setHint(guess);
                    setCode(guess.code);
                } else {
                    setError("Couldn't infer a nearest airport — please enter manually.");
                }
            },
            (err) => setError(err.message || "Location permission denied."),
            { timeout: 8000 }
        );
    };

    const save = async () => {
        if (!code || code.length < 3) { setError("Please enter a 3-letter IATA code."); return; }
        setBusy(true); setError("");
        try {
            await api.post("/me/preferred-airport", { preferred_airport: code.toUpperCase() });
            onUpdated?.(code.toUpperCase());
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="mt-6 bg-surface border border-ink/10 rounded-2xl p-8" data-testid="preferred-airport-card">
            <div className="sp-overline flex items-center gap-2">
                <Plane strokeWidth={1.5} className="w-3.5 h-3.5" />
                Departure airport
            </div>
            <div className="mt-3 flex items-center gap-3">
                <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 4))}
                    placeholder="IATA — e.g. JFK"
                    className="bg-transparent border border-ink/15 rounded-full px-4 py-2 text-base font-mono w-40 focus:outline-none focus:border-ink"
                    data-testid="preferred-airport-input"
                />
                <button onClick={save} disabled={busy} className="sp-btn-primary text-xs px-4 py-2" data-testid="preferred-airport-save">
                    {busy ? "Saving…" : "Save"}
                </button>
                <button onClick={detect} className="sp-btn-ghost text-xs px-4 py-2" data-testid="preferred-airport-detect" type="button">
                    <MapPin strokeWidth={1.5} className="w-3.5 h-3.5" />
                    Use my location
                </button>
            </div>
            {hint && <div className="text-xs text-ink-muted mt-3">Nearest hub: <span className="font-mono">{hint.code}</span> — {hint.name}</div>}
            {error && <div className="text-xs text-red-700 mt-3" data-testid="preferred-airport-error">{error}</div>}
            <p className="text-xs text-ink-muted mt-3">Flight recommendations search from this airport. Defaults to JFK if unset.</p>
        </div>
    );
}
